import { HttpTypes } from '@/types';
import { AxiosInstance, AxiosRequestConfig } from 'axios';

class Cart {
  private client: AxiosInstance;
  private routePath = '/sites/cart';

  public host = '';

  constructor(client: AxiosInstance) {
    client.interceptors.request.use();

    this.client = client;
  }

  interceptRequest(config: AxiosRequestConfig) {
    config.headers = {
      ...config.headers,
    };

    return config;
  }

  async list(
    cartId: string,
    headers?: Record<string, unknown>,
    locationId?: string,
    token?: string
  ): Promise<HttpTypes.IResponse<HttpTypes.StoreCartResponse['data']>> {
    this.interceptRequest({ ...this.client.request });

    const header = {
      ...headers,
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get(`${this.routePath}/get/${cartId}`, {
      headers: header,
    });

    return result.data;
  }

  async detail(
    checkoutId: string,
    shippingInsurance: boolean,
    token?: string
  ): Promise<HttpTypes.StoreCartDetailResponse> {
    this.interceptRequest({ ...this.client.request });
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get(`${this.routePath}/detail/${checkoutId}`, {
      params: {
        is_include_insurance: shippingInsurance,
      },
      headers,
    });
    return result.data;
  }

  async create(
    header: Record<string, unknown>,
    token?: string,
    cartId?: string
  ): Promise<HttpTypes.IResponse<HttpTypes.StoreCartResponse['data']>> {
    this.interceptRequest({ ...this.client.request });

    const headers = {
      ...header,
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'Content-Type': 'application/json',
      'X-Cart-Id': cartId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.post(
      `${this.routePath}/create`,
      {},
      {
        headers,
      }
    );

    return result.data;
  }
  async addItem(
    cartId: string,
    payload: HttpTypes.StoreCartPayload,
    token?: string
  ): Promise<HttpTypes.IResponse<HttpTypes.StoreCartResponse['data']>> {
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.post(`${this.routePath}/add/${cartId}`, payload, {
      headers,
    });
    return result.data;
  }

  async updateItem(
    cartId: string,
    payload: HttpTypes.StoreCart,
    token?: string
  ): Promise<HttpTypes.StoreCartResponse> {
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const result = await this.client.put(`${this.routePath}/update/${cartId}`, payload, {
      headers,
    });
    return result.data;
  }
  async buyNow(
    payload: HttpTypes.StoreCartPayload,
    token?: string,
    locationId?: string
  ): Promise<HttpTypes.StoreCartBuyNowResponse> {
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'Content-Type': 'application/json',
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.post(`${this.routePath}/buy-now`, payload, {
      headers,
    });
    return result.data;
  }

  async removeItem(
    cartId: string,
    id: string,
    token?: string
  ): Promise<HttpTypes.StoreCartResponse> {
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.delete(`${this.routePath}/remove/${cartId}`, {
      data: { id },
      headers: headers,
    });
    return result.data;
  }

  async clear(cartId: string): Promise<HttpTypes.StoreCartResponse> {
    const result = await this.client.post(`${this.routePath}/clear`, {
      id: cartId,
    });
    return result.data;
  }
}

export default Cart;
